import type { CallbackInterface } from 'recoil';
import { selectorFamily } from 'recoil';
import type { BlockId } from '../types';
import type { CustomBenchmarkTypeEnum } from 'venn-api';
import { blockSettings } from './blockSettings';
import { FULLY_DISABLED_BENCHMARK_BLOCKS, hasDisabledIndividualBenchmark } from 'venn-utils';
import type { BenchmarkInputId } from './input-management/benchmarkInput';
import {
  blockBenchmarkInput,
  benchmarkInputSubject,
  benchmarkInputIsRelative,
  benchmarkInputType,
  benchmarkInputs,
  benchmarkInputName,
} from './input-management/benchmarkInput';
import { allBlockIdsState } from '../grid';

const DEFAULT_RELATIVE_VALUE = false;

export const blockBenchmarkRelative = selectorFamily<boolean, BlockId>({
  key: 'blockBenchmarkRelative',
  get:
    (id) =>
    ({ get }) => {
      if (
        get(blockBenchmarkType(id)) === 'INDIVIDUAL' &&
        hasDisabledIndividualBenchmark(get(blockSettings(id)).customBlockType)
      ) {
        return DEFAULT_RELATIVE_VALUE;
      }

      const blockType = get(blockSettings(id)).customBlockType;
      if (FULLY_DISABLED_BENCHMARK_BLOCKS.includes(blockType)) {
        return DEFAULT_RELATIVE_VALUE;
      }

      return get(blockBenchmarkRelativeRawValue(id));
    },
});

export const blockBenchmarkRelativeRawValue = selectorFamily<boolean, BlockId>({
  key: 'blockBenchmarkRelative',
  get:
    (id) =>
    ({ get }) => {
      const inputId = get(blockBenchmarkInput(id));
      return inputId ? get(benchmarkInputIsRelative(inputId)) : DEFAULT_RELATIVE_VALUE;
    },
});

const DEFAULT_TYPE: CustomBenchmarkTypeEnum = 'NONE';

export const blockBenchmarkType = selectorFamily<CustomBenchmarkTypeEnum, BlockId>({
  key: 'blockBenchmarkType',
  get:
    (id) =>
    ({ get }) => {
      const inputId = get(blockBenchmarkInput(id));
      const blockType = get(blockSettings(id)).customBlockType;
      return inputId && !FULLY_DISABLED_BENCHMARK_BLOCKS.includes(blockType)
        ? get(benchmarkInputType(inputId))
        : DEFAULT_TYPE;
    },
});

/** use this in a useRecoilCallback hook to delete benchmarks */
export const onBenchmarkInputGroupDelete =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async (groupId: BenchmarkInputId) => {
    set(benchmarkInputs, (ids) => ids.filter((id) => id !== groupId));

    reset(benchmarkInputName(groupId));
    reset(benchmarkInputSubject(groupId));
    reset(benchmarkInputType(groupId));

    const blockIds = await snapshot.getPromise(allBlockIdsState);
    blockIds.forEach((blockId) =>
      set(blockBenchmarkInput(blockId), (current) => (current === groupId ? undefined : current)),
    );
  };
